import { MOTION_GRAPHICS_COLLECTION } from './collections';
import { parseFilterBy } from './filter-utils';
import type { SortOption } from './typesense-sort';
import { getSortBy } from './typesense-sort';

const QUERY_BY = 'name, styles, themes, types, keywords, compatibility';

const QUERY_BY_WEIGHTS =
  'name:4, styles:3, themes:3, types:3, keywords:2, compatibility:2';

// Page 1 of the featured path is a dedicated block of featured assets
const FEATURED_PAGE_SIZE = 36;
const DEFAULT_FEATURED_SEED = 1;

export const isFeaturedSearchPath = ({
  featuredPageOne,
  sort,
  hasSearch,
}: {
  featuredPageOne: boolean;
  sort: SortOption;
  hasSearch: boolean;
}): boolean => featuredPageOne && !hasSearch && sort === 'relevancy';

export const getSearches = ({
  q,
  filter_by,
  sort,
  fetchFreeAndPremium,
  isHomePage = false,
  page = 1,
  perPage,
  featuredPageOne = false,
  seed,
}: {
  q?: string;
  filter_by: string;
  sort: SortOption;
  fetchFreeAndPremium: boolean;
  isHomePage?: boolean;
  page?: number;
  // When set, overrides the default per-collection sizes. For
  // fetchFreeAndPremium it splits across free + premium; otherwise it sets
  // the single-query page size. The defaults (8 premium / 16 free / 24
  // single) are preserved when perPage is undefined.
  perPage?: number;
  featuredPageOne?: boolean;
  seed?: number;
}) => {
  const { search, filter } = parseFilterBy(filter_by);
  const hasSearchTerms = search.length > 0;

  const useFeatured = isFeaturedSearchPath({
    featuredPageOne,
    sort,
    hasSearch: hasSearchTerms,
  });

  const queryText = q || (hasSearchTerms ? search.join(', ') : '*');

  // Fields shared by every query in this request. Per-query overrides
  // (per_page/filter_by/sort_by, and page for the featured block) are spread on
  // top.
  const baseQuery = {
    q: queryText,
    query_by: QUERY_BY,
    query_by_weights: QUERY_BY_WEIGHTS,
    collection: MOTION_GRAPHICS_COLLECTION,
    exhaustive_search: false,
    drop_tokens_threshold: 0,
    num_typos: 0,
    page,
  };

  // In featured mode the stream (pages 2+) must exclude featured assets
  const streamBaseFilter = useFeatured
    ? filter
      ? `${filter} && is_featured:false`
      : 'is_featured:false'
    : filter || undefined;
  const featuredFirst = useFeatured ? false : isHomePage;

  const streamQueries = fetchFreeAndPremium
    ? [
        {
          ...baseQuery,
          per_page: perPage ? Math.floor(perPage / 2) : 8,
          filter_by: streamBaseFilter
            ? `${streamBaseFilter} && is_premium:true`
            : 'is_premium:true',
          sort_by: getSortBy(sort, true, featuredFirst),
        },
        {
          ...baseQuery,
          per_page: perPage ? Math.ceil(perPage / 2) : 16,
          filter_by: streamBaseFilter
            ? `${streamBaseFilter} && is_premium:false`
            : 'is_premium:false',
          sort_by: getSortBy(sort, false, featuredFirst),
        },
      ]
    : [
        {
          ...baseQuery,
          per_page: perPage ?? 24,
          filter_by: streamBaseFilter,
          sort_by: getSortBy(sort, true, featuredFirst),
        },
      ];

  if (!useFeatured) {
    return streamQueries;
  }

  const featuredQuery = {
    ...baseQuery,
    per_page: FEATURED_PAGE_SIZE,
    filter_by: filter ? `${filter} && is_featured:true` : 'is_featured:true',
    sort_by: `_rand(${seed ?? DEFAULT_FEATURED_SEED}):asc`,
    page: 1,
  };

  return [featuredQuery, ...streamQueries];
};

export const applyPageParam = <S extends { page?: number }>(
  searches: S[],
  pageParam: number,
  featuredFirstPage: boolean
): S[] => {
  if (!featuredFirstPage) {
    return searches.map((query) => ({ ...query, page: pageParam }));
  }

  return pageParam === 1
    ? [{ ...searches[0], page: 1 }]
    : searches.slice(1).map((query) => ({ ...query, page: pageParam - 1 }));
};
